/**
 * FinancialAssumptionsForm — FA pack, FA3 (manual structured form).
 *
 * Canonical edit surface for `site.financialAssumptions`. Used by all
 * three entry paths:
 *   - Post-extraction review (FA5) commits, then opens the form for
 *     follow-up edits.
 *   - Manual entry path (FA3 picker → form empty).
 *   - Industry-baseline path (FA4 picker → form pre-filled).
 *
 * Design discipline:
 *   - Inline editing, NO modal. Tab order = top-to-bottom.
 *   - Provenance chip next to every field. Editing flips the chip to
 *     `manual`.
 *   - Outlier highlight using `isOutlier(tech, field, value)` —
 *     amber chip with "verify or note".
 *   - Numeric inputs are nullable. The form's reducer treats empty
 *     string as null (i.e. unset).
 *   - Save / Cancel are explicit. Saves go through the store action
 *     `updateSiteFinancialAssumptions` (full replace).
 *
 * Sections (collapsible):
 *   1. Project basics
 *   2. Capital costs
 *   3. Operating costs
 *   4. Financing
 *   5. Revenue (PPA)
 *   6. Output metrics  (auto-derived when WACC + CAPEX + life + CF
 *                       are all set; the form lights up the derived
 *                       chip — see FA11 recompute()).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, FileSpreadsheet, Info, Plus, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useStore } from '../../../store';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import type {
  FieldProvenance,
  FieldProvenanceConfidence,
  FieldProvenanceSource,
  FinancialAssumptions,
  FinancialField,
  FinancialPhase,
  FinancialTechnology,
} from '../../../types/financialAssumptions';
import {
  FINANCIAL_MODEL_VERSION_CURRENT,
  applyCampusPatch,
  defaultNextFinancialPhase,
  deriveCapexPerKwUsd,
  emptyFinancialAssumptions,
  migrateFinancialAssumptionsRecord,
  readFinancialAssumptions,
  sortFinancialPhases,
  finalPhaseCapacityMw,
  financialPhaseIncrementMw,
  syncCampusRollupFromPhases,
} from '../../../types/financialAssumptions';
import type { CrossValidationFinding } from '../../../types/financialAssumptions';
import { applySync, crossValidateFinancials, findingsBySeverity } from '../../../lib/crossValidateFinancials';
import type { ProjectIntent } from '../../../types/siteRag';
import { isOutlier } from '../../../services/financial/industryBaselines';
import { recompute } from '../../../lib/projectFinance';

const TECH_OPTIONS: Array<{ value: FinancialTechnology; label: string }> = [
  { value: 'tbd',         label: 'To be determined' },
  { value: 'smr',         label: 'Small Modular Reactor (SMR)' },
  { value: 'gas-cc',      label: 'Gas combined cycle' },
  { value: 'gas-sc',      label: 'Gas simple cycle (peaker)' },
  { value: 'solar-bess',  label: 'Solar + BESS' },
  { value: 'wind-bess',   label: 'Wind + BESS' },
  { value: 'mixed',       label: 'Hybrid / mixed' },
];

type FieldKind = 'number' | 'percent' | 'usd' | 'usd-per-kw' | 'usd-per-mwh' | 'mw' | 'years' | 'months' | 'date';

interface FieldDef {
  field: FinancialField;
  label: string;
  kind: FieldKind;
  /** Inline helper hint rendered under the input. */
  hint?: string;
}

type PhaseFieldKey =
  | 'capacityMW'
  | 'capacityFactorPct'
  | 'inServiceDate'
  | 'capexTotalUsd'
  | 'capexPerKwUsd'
  | 'constructionMonths';

interface PhaseFieldDef {
  field: PhaseFieldKey;
  label: string;
  kind: FieldKind;
  hint?: string;
}

const PHASE_FIELDS: PhaseFieldDef[] = [
  { field: 'capacityMW',         label: 'Capacity',           kind: 'mw' },
  { field: 'capacityFactorPct',  label: 'Capacity factor',    kind: 'percent', hint: 'Annual energy / nameplate × hours' },
  { field: 'inServiceDate',      label: 'In-service date',    kind: 'date' },
  { field: 'capexTotalUsd',        label: 'Phase CAPEX',           kind: 'usd' },
  { field: 'capexPerKwUsd',        label: 'CAPEX / kW',            kind: 'usd-per-kw', hint: 'Auto-derived when total + capacity are set' },
  { field: 'constructionMonths',   label: 'Construction period',   kind: 'months' },
];

/**
 * Per-phase fields that count toward the header "N of M fields set" tally.
 * `capexPerKwUsd` is excluded because it is read-only / auto-derived from
 * `capexTotalUsd` + `capacityMW` — counting it would double-count CAPEX.
 */
const COUNTABLE_PHASE_FIELDS: PhaseFieldKey[] = PHASE_FIELDS
  .map(f => f.field)
  .filter(f => f !== 'capexPerKwUsd');

const SECTIONS: Array<{ key: string; title: string; fields: FieldDef[] }> = [
  {
    key: 'basics',
    title: 'Campus-wide basics',
    fields: [
      { field: 'projectLifeYears',   label: 'Project life',       kind: 'years' },
    ],
  },
  {
    key: 'capital',
    title: 'Campus-wide capital',
    fields: [
      { field: 'decommissioningUsd',   label: 'Decommissioning cost',  kind: 'usd' },
      { field: 'workingCapitalUsd',    label: 'Working capital',       kind: 'usd' },
      { field: 'contingencyPct',       label: 'Contingency',           kind: 'percent' },
    ],
  },
  {
    key: 'operating',
    title: 'Operating costs',
    fields: [
      { field: 'fixedOmPerKwYearUsd',     label: 'Fixed O&M',     kind: 'usd-per-kw' },
      { field: 'variableOmPerMwhUsd',     label: 'Variable O&M',  kind: 'usd-per-mwh' },
      { field: 'fuelCostPerMwhUsd',       label: 'Fuel cost',     kind: 'usd-per-mwh' },
      { field: 'insuranceAndPropertyTaxPct', label: 'Insurance & property tax', kind: 'percent' },
    ],
  },
  {
    key: 'financing',
    title: 'Financing',
    fields: [
      { field: 'debtToEquityRatio',  label: 'Debt fraction (0-1)',  kind: 'number', hint: '0.7 = 70:30 D:E' },
      { field: 'debtRatePct',        label: 'Debt rate',            kind: 'percent' },
      { field: 'equityHurdlePct',    label: 'Equity hurdle',        kind: 'percent' },
      { field: 'waccPct',            label: 'WACC',                 kind: 'percent', hint: 'Auto-derived when D:E + rates + tax are set' },
      { field: 'taxRatePct',         label: 'Tax rate',             kind: 'percent' },
      { field: 'itcPct',             label: 'ITC',                  kind: 'percent', hint: 'Investment Tax Credit' },
      { field: 'ptcPerMwhUsd',       label: 'PTC',                  kind: 'usd-per-mwh', hint: 'Production Tax Credit, year-1 nominal' },
    ],
  },
  {
    key: 'revenue',
    title: 'Revenue (PPA)',
    fields: [
      { field: 'ppaPricePerMwhUsd',       label: 'PPA price',     kind: 'usd-per-mwh' },
      { field: 'ppaEscalatorPctPerYear',  label: 'PPA escalator', kind: 'percent' },
      { field: 'ppaTermYears',            label: 'PPA term',      kind: 'years' },
    ],
  },
  {
    key: 'outputs',
    title: 'Output metrics',
    fields: [
      { field: 'lcoeNominalPerMwhUsd', label: 'LCOE (nominal)', kind: 'usd-per-mwh' },
      { field: 'lcoeRealPerMwhUsd',    label: 'LCOE (real)',    kind: 'usd-per-mwh' },
      { field: 'npvUsd',               label: 'NPV',            kind: 'usd' },
      { field: 'projectIrrPct',        label: 'Project IRR',    kind: 'percent' },
      { field: 'equityIrrPct',         label: 'Equity IRR',     kind: 'percent' },
      { field: 'paybackYears',         label: 'Payback',        kind: 'years' },
    ],
  },
];

export interface FinancialAssumptionsFormProps {
  /** Existing FA, or null when the user is starting from scratch. */
  initial: FinancialAssumptions | null;
  /** Site id — used by read/write normalization (R-58). */
  siteId?: string;
  /**
   * DRAFT-ONLY SCOPE (audit C12). When true the form edits an object that is
   * NOT the site's financial model, and every affordance that would write the
   * site record is withheld.
   *
   * The pathway comparison's "Detailed assumptions" dialog says "Changes apply
   * to the comparison draft only" and then rendered this form with the real
   * `siteId`. "Pull from BOM + Intent" calls `pullFinancialFromSources(siteId)`,
   * a STORE ACTION that persists to the site — so a button inside a
   * draft-scoped dialog silently rewrote the project's actual assumptions, and
   * the dialog's own promise was false. The pull also has nothing coherent to
   * do here: it fills a SITE's phases from the site's BoM and intent, while
   * this draft describes one benchmark pathway.
   */
  draftOnly?: boolean;
  /** Optional project intent — phase linking + ordering warnings. */
  projectIntent?: ProjectIntent;
  /** Called when the user clicks Save. Receives the next FA. */
  onSave: (next: FinancialAssumptions) => void | Promise<void>;
  /** Called when the user clicks Cancel. Optional — host can omit. */
  onCancel?: () => void;
  /** Identifier of the editor (uid / email) — stamped on the FA. */
  updatedBy: string;
  /**
   * Auto-populate hook (Financial auto-populate / R-58). Defaults to the
   * `pullFinancialFromSources` store action; injectable so tests can drive
   * the button without a live store. The form re-seeds its draft from the
   * (store-)updated FA on success.
   *
   * INVESTMENT-GRADE HARD RULE: this path only fills blanks / refreshes
   * `'derived'` cells from REAL sources (BOM rollup + project-intent). It
   * never fabricates a value and never clobbers a user `'manual'` field —
   * the guarantee is enforced by the pure aggregator behind the action.
   */
  onPullFromSources?: (
    siteId: string,
  ) => Promise<
    | {
        ok: true;
        phaseCount: number;
        filledFieldCount: number;
        skippedUnsourcedCount: number;
        /** CBE third-source counts (optional — older injected hosts omit them). */
        contextFilledCount?: number;
        contextConflictCount?: number;
        contextRangeHintCount?: number;
      }
    | { ok: false; error: string }
  >;
}

/** Per-phase provenance key — mirrors the aggregator's `${phaseId}:${field}`. */
function phaseProvenanceKey(phaseId: string, field: string): string {
  return `${phaseId}:${field}`;
}

/** Normalise an incoming FA prop into the draft shape (phase-authoritative). */
function seedDraft(
  siteId: string,
  initial: FinancialAssumptions | null | undefined,
  updatedBy: string,
): FinancialAssumptions {
  const seed = initial ?? emptyFinancialAssumptions(new Date().toISOString(), updatedBy);
  return readFinancialAssumptions(siteId, seed) ?? seed;
}

/**
 * Identity of an incoming FA revision. `updatedAt` is stamped by every store
 * writer (`writeFinancialAssumptions`), so a change in it means SOMEONE ELSE's
 * write landed — the LIVE-SITES `onSnapshot` listener delivers other tabs' and
 * other users' saves into this client while the form is open.
 */
function faRevision(fa: FinancialAssumptions | null | undefined): string | null {
  return fa?.updatedAt ?? null;
}

/** Severity rank for the findings strip — errors first, info last. */
const FINDING_SEVERITY_ORDER: ReadonlyArray<CrossValidationFinding['severity']> = [
  'error',
  'warning',
  'info',
];

export function FinancialAssumptionsForm({
  initial,
  siteId = 'local',
  draftOnly = false,
  projectIntent,
  onSave,
  onCancel,
  updatedBy,
  onPullFromSources,
}: FinancialAssumptionsFormProps): React.JSX.Element {
  const [draft, setDraft] = useState<FinancialAssumptions>(
    () => seedDraft(siteId, initial, updatedBy),
  );
  // ── Stale-draft / concurrency state (audit A6 / plan 1.3) ────────────────
  // `dirty` = the local draft carries unsaved edits. `incoming` = an FA
  // revision that arrived from the store while the draft was dirty; it is held
  // (never auto-applied, never auto-discarded) until the user chooses.
  const [dirty, setDirty] = useState(false);
  const [incoming, setIncoming] = useState<FinancialAssumptions | null>(null);
  /** Last FA revision this form has SEEN (applied or offered). */
  const seenRevisionRef = useRef<string | null>(faRevision(initial));
  const [pullConfirm, setPullConfirm] = useState(false);
  /**
   * Bumped on every whole-draft REPLACEMENT (re-seed, adopt-theirs, pull).
   * It is part of the input keys, so the numeric inputs remount and drop their
   * focus-scoped raw buffers — without it a field the user was mid-edit in
   * would keep rendering the abandoned keystrokes over the adopted value.
   */
  const [seedGeneration, setSeedGeneration] = useState(0);
  const [activePhaseIdx, setActivePhaseIdx] = useState(0);
  const phases = useMemo(
    () => sortFinancialPhases(draft.phases ?? []),
    [draft.phases],
  );
  const activePhase = phases[activePhaseIdx] ?? phases[0];
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    basics: true,
    capital: false,
    operating: false,
    financing: false,
    revenue: false,
    outputs: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pulling, setPulling] = useState(false);

  // Auto-populate source availability — read straight from the store by
  // siteId so the button's enabled state tracks the SAME authoritative
  // sources the `pullFinancialFromSources` action consumes (BOM line items
  // + project-intent phases), regardless of which props the host forwarded.
  // Subscribe narrowly (booleans only) so unrelated store mutations don't
  // re-render the form.
  const hasPullSources = useStore((s) => {
    // C12 — a draft-scoped form never offers a site-writing pull.
    if (draftOnly) return false;
    const site = s.siteRag.sites.find(st => st.id === siteId);
    if (!site) return false;
    const hasBom = Boolean(site.supplyChainPlan && site.supplyChainPlan.bom.length > 0);
    const hasIntentPhases = Boolean(
      site.projectIntent?.phases && site.projectIntent.phases.length > 0,
    );
    // CBE third source — tagged site-context claims alone are a real source
    // (the store action accepts a claims-only pull), so they enable the button.
    const hasContextClaims = Boolean(
      site.siteContext?.claims && site.siteContext.claims.length > 0,
    );
    return hasBom || hasIntentPhases || hasContextClaims;
  });

  // ── Re-seed on an incoming FA revision (audit A6 / plan 1.3) ─────────────
  //
  // The draft used to be seeded ONCE. `handleSave` then sent the WHOLE draft and
  // `updateSite` replaced the nested map, so a save could silently revert an
  // edit another actor had already committed — and the form kept rendering the
  // stale record after any out-of-band write (auto-pull, BoM sync, the
  // cross-validation "Sync to intent" button).
  //
  // CLEAN draft  ⇒ adopt the new revision silently (nothing to lose).
  // DIRTY draft  ⇒ hold it in `incoming` and let the user choose. Never
  //                clobber either side without an explicit click.
  useEffect(() => {
    const revision = faRevision(initial);
    if (revision === seenRevisionRef.current) return;
    seenRevisionRef.current = revision;
    if (dirty) {
      setIncoming(initial ?? null);
      return;
    }
    setDraft(seedDraft(siteId, initial, updatedBy));
    setSeedGeneration(g => g + 1);
    setIncoming(null);
  }, [initial, dirty, siteId, updatedBy]);

  /** Adopt the revision that arrived while editing — discards the local draft. */
  const handleAdoptIncoming = useCallback(() => {
    // Guard: with nothing held there is nothing to adopt, and seeding from
    // `null` would blank the form rather than load "their version".
    if (!incoming) return;
    setDraft(seedDraft(siteId, incoming, updatedBy));
    setSeedGeneration(g => g + 1);
    setIncoming(null);
    setDirty(false);
    setError(null);
  }, [incoming, siteId, updatedBy]);

  /** Keep the local draft — the next Save deliberately overwrites theirs. */
  const handleKeepMine = useCallback(() => setIncoming(null), []);

  const setField = useCallback(<K extends FinancialField>(field: K, raw: string) => {
    setDirty(true);
    setDraft(prev => {
      const fieldDef = SECTIONS.flatMap(s => s.fields).find(f => f.field === field);
      const kind = fieldDef?.kind;
      const value = parseFieldValue(kind, raw);
      // String fields (date, technology) coerce differently.
      const nextValue = value === '__INVALID__' ? prev[field] : value;
      const provenance: FieldProvenance = {
        source: 'manual',
        confidence: 'high',
        extractedAt: new Date().toISOString(),
      };
      return {
        ...prev,
        [field]: nextValue,
        fieldProvenance: {
          ...prev.fieldProvenance,
          [field]: provenance,
        },
      };
    });
  }, []);

  const setTechnology = useCallback((tech: FinancialTechnology) => {
    setDirty(true);
    setDraft(prev => ({
      ...prev,
      technology: tech,
      fieldProvenance: {
        ...prev.fieldProvenance,
        technology: { source: 'manual', confidence: 'high', extractedAt: new Date().toISOString() },
      },
    }));
  }, []);

  const setPhaseField = useCallback((
    phaseId: string,
    field: PhaseFieldKey,
    raw: string,
  ) => {
    setDirty(true);
    setDraft(prev => {
      const now = new Date().toISOString();
      const nextPhases = (prev.phases ?? []).map(p => {
        if (p.phaseId !== phaseId) return p;
        const def = PHASE_FIELDS.find(f => f.field === field);
        const parsed = def ? parseFieldValue(def.kind, raw) : raw;
        const value = parsed === '__INVALID__' ? p[field] : parsed;
        return { ...p, [field]: value } as FinancialPhase;
      });
      // Per-phase $/kW must divide the phase's INCREMENTAL CAPEX by its
      // INCREMENTAL MW (the new plant added this phase), NOT the cumulative
      // capacity — under the cumulative convention a late phase's cumulative
      // figure would make $/kW artificially low. Recompute after the row edit
      // (and re-derive every row, since an earlier phase's edit shifts a later
      // phase's increment). See syncCapexFromSupplyChain for the same rule.
      const sorted = sortFinancialPhases(nextPhases);
      const idxByPhaseId = new Map(sorted.map((p, i) => [p.phaseId, i] as const));
      const rederivesPerKw = field === 'capexTotalUsd' || field === 'capacityMW';
      const derived = nextPhases.map(p => {
        if (!rederivesPerKw) return p;
        const i = idxByPhaseId.get(p.phaseId);
        const incr = i != null ? financialPhaseIncrementMw(sorted, i) : null;
        return { ...p, capexPerKwUsd: deriveCapexPerKwUsd(p.capexTotalUsd, incr) };
      });

      // ── PROVENANCE INTEGRITY (audit A5 / plan 1.2a) ──────────────────────
      // This edit is a USER edit, so the cell becomes `manual`. Without the
      // stamp the cell kept whatever chip the last auto-pull left on it —
      // and the pull's protection rule is `prov?.source === 'derived'`, so the
      // NEXT "Pull from BOM + Intent" silently overwrote the number the user
      // had just typed while still showing a `derived` chip beside it.
      //
      // The $/kW cells this edit RE-DERIVES are stamped `derived` for the same
      // reason in reverse: they are not user-authored, so a later pull is free
      // to refresh them.
      const phaseFieldProvenance: Record<string, FieldProvenance> = {
        ...(prev.phaseFieldProvenance ?? {}),
        [phaseProvenanceKey(phaseId, field)]: {
          source: 'manual',
          confidence: 'high',
          extractedAt: now,
        },
      };
      if (rederivesPerKw) {
        for (const p of derived) {
          phaseFieldProvenance[phaseProvenanceKey(p.phaseId, 'capexPerKwUsd')] = {
            source: 'derived',
            confidence: 'high',
            cellReference: 'capexTotalUsd / capacityMW increment',
            extractedAt: now,
          };
        }
      }

      return syncCampusRollupFromPhases({
        ...prev,
        phases: derived,
        phaseFieldProvenance,
        modelVersion: 2,
      });
    });
  }, []);

  // Per-phase technology override. An empty string clears the override so the
  // phase falls back to (inherits) the campus technology — we store `undefined`
  // for that case rather than fabricating a concrete bucket.
  const setPhaseTechnology = useCallback((phaseId: string, raw: string) => {
    setDirty(true);
    setDraft(prev => {
      const nextPhases = (prev.phases ?? []).map(p => {
        if (p.phaseId !== phaseId) return p;
        const next: FinancialPhase = { ...p };
        if (raw === '') {
          delete next.technology;
        } else {
          next.technology = raw as FinancialTechnology;
        }
        return next;
      });
      return syncCampusRollupFromPhases({ ...prev, phases: nextPhases, modelVersion: 2 });
    });
  }, []);

  const handleAddPhase = useCallback(() => {
    setDirty(true);
    setDraft(prev => {
      const sorted = sortFinancialPhases(prev.phases ?? []);
      const prior = sorted[sorted.length - 1];
      const next = defaultNextFinancialPhase(prior, prev.technology);
      const phasesNext = [...sorted, next];
      setActivePhaseIdx(phasesNext.length - 1);
      return syncCampusRollupFromPhases({ ...prev, phases: phasesNext, modelVersion: 2 });
    });
  }, []);

  const phaseFindings = useMemo(() => crossValidateFinancials({
    financial: draft,
    financialPhases: phases,
    projectIntent: projectIntent ? {
      powerRequirement: projectIntent.powerRequirement,
      powerRequirementExactMW: projectIntent.powerRequirementMW ?? undefined,
      powerRequirementMW: projectIntent.powerRequirementMW, // SSOT-SENTINEL-OK
      nuclearInterest: projectIntent.nuclearInterest,
      phases: projectIntent.phases?.map(p => ({
        id: p.id,
        sequence: p.sequence,
        capacityMW: p.capacityMW,
        targetInServiceDate: p.targetInServiceDate,
      })),
    } : undefined,
  }), [draft, phases, projectIntent]);

  // ── Cross-validation findings strip (audit E1) ───────────────────────────
  // The form used to compute the WHOLE finding set and render exactly ONE of
  // them (the phase-ordering warning), so the magnitude caps, the CAPEX/CF
  // band checks and the intent-capacity mismatches were computed and thrown
  // away — "$320T working capital passes silently". Render them all, errors
  // first, using the library's own severity filter.
  const orderedFindings = useMemo(
    () => FINDING_SEVERITY_ORDER.flatMap(sev => findingsBySeverity(phaseFindings, sev)),
    [phaseFindings],
  );

  /**
   * One-click resolution for a finding that carries a `sync` targeting the FA.
   * PHASE-AWARE: rollup-derived campus fields must land on a phase
   * (`applyCampusPatch`) or the next save discards them — this is the same
   * refusal contract the store writer enforces, applied to the draft.
   */
  const handleApplySync = useCallback((finding: CrossValidationFinding) => {
    const sync = finding.sync;
    if (!sync || sync.targetSurface !== 'financialAssumptions') return;
    const { financialPatch } = applySync(sync, { financial: draft });
    if (!financialPatch) return;
    const patch = { [sync.field]: financialPatch[sync.field as keyof typeof financialPatch] };
    const { next, unapplied } = applyCampusPatch(draft, patch);
    if (unapplied.length > 0) {
      setError(
        `"${unapplied.join(', ')}" is derived from this model's ${(draft.phases ?? []).length} phases — `
        + 'edit it on the phase you mean rather than at campus level.',
      );
      return;
    }
    setError(null);
    setDirty(true);
    setDraft({
      ...next,
      fieldProvenance: {
        ...next.fieldProvenance,
        [sync.field]: {
          source: 'manual',
          confidence: 'high',
          extractedAt: new Date().toISOString(),
        } satisfies FieldProvenance,
      },
    });
  }, [draft]);

  // Live recompute — drives the "derived" provenance hint on output rows.
  const recomputed = useMemo(() => recompute(draft), [draft]);
  // CUMULATIVE convention: campus capacity = FINAL/MAX cumulative phase
  // capacity (ultimate campus size), NOT the sum across phases.
  const totalMw = finalPhaseCapacityMw(phases);

  // Phase-aware "N of M fields set" — counts populated per-phase fields
  // (capacityMW / capexTotalUsd / inServiceDate / …) across EVERY phase in
  // addition to the campus-level fields + technology, so populating Phase 1
  // (or an auto-pull) is reflected in the header counter. capexPerKwUsd is
  // derived (read-only), so it is excluded from both the set + total tallies.
  const setCount = useMemo(() => countSetFields(draft, phases), [draft, phases]);
  const totalCount = useMemo(
    () =>
      SECTIONS.flatMap(s => s.fields).length +
      1 /* tech */ +
      phases.length * COUNTABLE_PHASE_FIELDS.length,
    [phases.length],
  );

  const toggleSection = useCallback((key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleSave = useCallback(async () => {
    setError(null);
    setSaving(true);
    try {
      await onSave({
        ...draft,
        updatedAt: new Date().toISOString(),
        updatedBy,
      });
      // The draft is now the persisted truth. Clearing `dirty` re-arms the
      // silent re-seed, so the revision the store echoes back (with its own
      // `updatedAt`) is adopted instead of being offered as a conflict.
      setDirty(false);
      setIncoming(null);
    } catch (e) {
      setError((e as Error)?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, updatedBy]);

  // ── Pull from BOM + Intent (Financial auto-populate / R-58) ──────────────
  // Calls the store action (the SSOT for the investment-grade hard rule),
  // then RE-SEEDS the draft from the freshly-updated store FA so the newly
  // auto-filled per-phase cells + provenance chips render immediately. On a
  // refusal (no source / not found / no phases) we surface the reason as a
  // toast + inline error without mutating the draft.
  //
  // A pull REPLACES the draft with the stored model, so with unsaved edits in
  // hand it is destructive: it used to discard them without asking (audit A6).
  // A dirty draft arms an explicit confirm first — see `handlePullClick`.
  const handlePull = useCallback(async () => {
    setError(null);
    setPullConfirm(false);
    setPulling(true);
    try {
      const pull = onPullFromSources
        ? await onPullFromSources(siteId)
        : await useStore.getState().pullFinancialFromSources(siteId);
      if (!pull.ok) {
        setError(pull.error);
        toast.error(`Could not auto-populate: ${pull.error}`);
        return;
      }
      // Re-seed the draft from the store's updated FA (the action persisted
      // via the FA write path). Fall back to keeping the current draft if the
      // site somehow has no FA after a successful pull.
      const updatedFa = useStore.getState().siteRag.sites.find(s => s.id === siteId)?.financialAssumptions;
      if (updatedFa) {
        const seeded = readFinancialAssumptions(siteId, updatedFa) ?? updatedFa;
        setDraft(seeded);
        setSeedGeneration(g => g + 1);
        // The draft now IS the stored revision (the user accepted the
        // replacement), so re-arm the silent re-seed and retire any pending
        // conflict — otherwise the banner would offer a revision we just took.
        seenRevisionRef.current = faRevision(updatedFa);
        setDirty(false);
        setIncoming(null);
      }
      const { filledFieldCount, skippedUnsourcedCount } = pull;
      const blankNote =
        skippedUnsourcedCount > 0
          ? `; ${skippedUnsourcedCount} field${skippedUnsourcedCount === 1 ? '' : 's'} left blank — no source`
          : '';
      // CBE third-source summary — appended only when nonzero so the
      // BOM+intent-only message stays unchanged.
      const ctxFilled = pull.contextFilledCount ?? 0;
      const ctxConflicts = pull.contextConflictCount ?? 0;
      const ctxRangeHints = pull.contextRangeHintCount ?? 0;
      const ctxParts: string[] = [];
      if (ctxFilled > 0) ctxParts.push(`${ctxFilled} from site context`);
      if (ctxConflicts > 0) ctxParts.push(`${ctxConflicts} context conflict${ctxConflicts === 1 ? '' : 's'} to review`);
      if (ctxRangeHints > 0) ctxParts.push(`${ctxRangeHints} range hint${ctxRangeHints === 1 ? '' : 's'} (enter a point value)`);
      const ctxNote = ctxParts.length > 0 ? `; ${ctxParts.join(', ')}` : '';
      toast.success(
        `Filled ${filledFieldCount} field${filledFieldCount === 1 ? '' : 's'} from BOM + intent${blankNote}${ctxNote}`,
      );
    } catch (e) {
      const msg = (e as Error)?.message ?? 'Auto-populate failed';
      setError(msg);
      toast.error(`Could not auto-populate: ${msg}`);
    } finally {
      setPulling(false);
    }
  }, [onPullFromSources, siteId]);

  /** Pull entry point — confirms first when the draft carries unsaved edits. */
  const handlePullClick = useCallback(() => {
    if (dirty) {
      setPullConfirm(true);
      return;
    }
    void handlePull();
  }, [dirty, handlePull]);

  return (
    <TooltipProvider delayDuration={200}>
    <div className="border border-border bg-surface-1 rounded-lg">
      <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 px-4 py-3 border-b border-border">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
            Financial Assumptions
          </div>
          <div className="text-[10px] text-text-muted mt-0.5">
            {setCount} of {totalCount} fields set
            {draft.source !== 'manual-entry' && (
              <span className="ml-2">· Source: {draft.source}</span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* C12 — the pull WRITES THE SITE, so a draft-scoped form must not
              offer it at all. Hidden rather than disabled: a greyed button
              invites the reader to look for the missing precondition, when the
              real answer is that this action does not belong in this scope. */}
          {draftOnly ? null : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePullClick}
                  disabled={pulling || !hasPullSources}
                  data-testid="fa-pull-from-sources"
                  className="gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  {pulling ? 'Pulling…' : 'Pull from BOM + Intent'}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-[10px] max-w-xs">
              {hasPullSources
                ? 'Fill blank per-phase fields from real sources only: BOM CAPEX rollup + project-intent capacity/dates. Never fabricates a value; never clobbers a manual edit.'
                : 'No source yet — add a priced BOM (Supply Chain) or project-intent phases to enable auto-populate.'}
            </TooltipContent>
          </Tooltip>
          )}
          {/* 6.1 — `sourceFileId` was WRITTEN and never READ. The extraction
              path parks the workbook in the Document Library specifically "so
              the `sourceFileHash` on the FA points at a file the user can
              re-open" (A11) — and then offered no way to re-open it. A model
              whose provenance is an uploaded spreadsheet is only auditable if
              the reader can get back to the spreadsheet. */}
          {draft.sourceFileId ? (
            <a
              href={`?tab=documents&doc=${encodeURIComponent(draft.sourceFileId)}`}
              className="hidden sm:inline-flex items-center gap-1 text-[10px] text-primary hover:underline whitespace-nowrap"
              data-testid="fa-source-workbook-link"
            >
              <FileSpreadsheet className="w-3 h-3" aria-hidden />
              Source workbook
            </a>
          ) : null}
          <div className="hidden sm:block text-[10px] text-text-muted whitespace-nowrap">
            {draft.updatedAt && <>Last updated {formatRelativeTime(draft.updatedAt)}</>}
          </div>
        </div>
        {/* F5 — BoM → FA STALENESS, WHERE THE USER CAN ACT ON IT.
            The write boundary already stamps `procurementCommercials.
            supersededAt` when the live plan is newer than the snapshot, and the
            REPORT says so in its procurement sentence — but the person editing
            the model saw nothing. Awarding a quote, re-seeding the BoM or
            syncing site works all move the plan under a CAPEX figure that keeps
            rendering as current here. */}
        {draft.procurementCommercials?.supersededAt ? (
          <div
            className="mt-2 px-2 py-1 text-[10px] rounded-md border border-warning/50 bg-warning/10 text-warning"
            data-testid="fa-bom-superseded"
          >
            The equipment plan has changed since these procurement terms were
            synced ({draft.procurementCommercials.supersededAt.slice(0, 10)}).
            Re-run &ldquo;Pull from BOM + Intent&rdquo; before relying on the CAPEX below.
          </div>
        ) : null}
      </div>

      <div className="px-4 py-3 space-y-3">
        {/* CHANGED ELSEWHERE (audit A6 / 1.3) — a newer revision arrived from
            the store while this draft had unsaved edits. Neither side is
            discarded until the user picks one. */}
        {incoming && (
          <div
            className="flex flex-wrap items-center gap-2 text-[11px] border border-warning/60 bg-warning/10 px-3 py-2 rounded-md"
            data-testid="fa-changed-elsewhere"
            role="status"
          >
            <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0" />
            <span className="text-text-primary">
              This model changed elsewhere
              {incoming.updatedBy ? ` (saved by ${incoming.updatedBy})` : ''} while you were
              editing. Your unsaved edits are still here.
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAdoptIncoming}
                data-testid="fa-changed-elsewhere-reload"
                className="gap-1.5"
              >
                <RefreshCw className="w-3 h-3" /> Load their version
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleKeepMine}
                data-testid="fa-changed-elsewhere-keep"
              >
                Keep editing mine
              </Button>
            </div>
          </div>
        )}

        {/* PULL CONFIRM (audit A6 / 1.3) — a pull replaces the draft with the
            stored model, so unsaved edits are lost. Ask first. */}
        {pullConfirm && (
          <div
            className="flex flex-wrap items-center gap-2 text-[11px] border border-warning/60 bg-warning/10 px-3 py-2 rounded-md"
            data-testid="fa-pull-confirm"
            role="alertdialog"
            aria-label="Confirm auto-populate"
          >
            <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0" />
            <span className="text-text-primary">
              You have unsaved edits. Pulling replaces this form with the stored model.
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handlePull()}
                data-testid="fa-pull-confirm-discard"
              >
                Discard my edits and pull
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPullConfirm(false)}
                data-testid="fa-pull-confirm-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-[10px] border border-border px-3 py-2 bg-surface-2 rounded-md">
          <span className="text-text-secondary">Campus summary</span>
          <span className="font-semibold text-text-primary">
            {totalMw != null ? `${totalMw} MW` : '— MW'}
          </span>
          <span className="text-text-secondary">·</span>
          <span className="text-text-muted">{phases.length} phase{phases.length !== 1 ? 's' : ''}</span>
          {dirty && (
            <span className="text-text-muted" data-testid="fa-draft-dirty">· unsaved edits</span>
          )}
        </div>

        {/* CROSS-VALIDATION FINDINGS (audit E1) — the whole set, errors first.
            Findings carrying an FA-targeted `sync` get a one-click fix that
            routes through `applyCampusPatch` (phase-authoritative). */}
        {orderedFindings.length > 0 && (
          <ul
            className="space-y-1.5"
            data-testid="fa-cross-validation-findings"
            aria-label="Cross-validation findings"
          >
            {orderedFindings.map(finding => (
              <CrossValidationFindingRow
                key={finding.id}
                finding={finding}
                onApplySync={handleApplySync}
              />
            ))}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="sm:w-36 shrink-0 border border-border p-2 space-y-1 rounded-md">
            <div className="text-[9px] uppercase tracking-widest text-text-muted mb-1">Phases</div>
            {phases.map((p, idx) => (
              <button
                key={p.phaseId}
                type="button"
                onClick={() => setActivePhaseIdx(idx)}
                className={`w-full text-left px-2 py-1.5 text-[11px] border rounded-md ${
                  idx === activePhaseIdx
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-transparent hover:bg-state-hover text-text-secondary'
                }`}
              >
                {p.label ?? `Phase ${p.sequence}`}
              </button>
            ))}
            <button
              type="button"
              onClick={handleAddPhase}
              className="w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] border border-dashed border-border text-text-muted hover:bg-state-hover rounded-md"
            >
              <Plus className="w-3 h-3" /> Add phase
            </button>
          </div>

          {activePhase && (
            <div className="flex-1 min-w-0 border border-border p-3 space-y-2 rounded-md">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-text-primary">
                {activePhase.label ?? `Phase ${activePhase.sequence}`}
              </div>
              <PhaseTechnologyRow
                phase={activePhase}
                campusTechnology={draft.technology}
                onChange={raw => setPhaseTechnology(activePhase.phaseId, raw)}
              />
              {PHASE_FIELDS.map(def => (
                <PhaseFieldRow
                  key={`${seedGeneration}-${activePhase.phaseId}-${def.field}`}
                  def={def}
                  phase={activePhase}
                  campusTechnology={draft.technology}
                  provenance={
                    draft.phaseFieldProvenance?.[phaseProvenanceKey(activePhase.phaseId, def.field)]
                  }
                  onChange={raw => setPhaseField(activePhase.phaseId, def.field, raw)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-[84px_minmax(0,1fr)] sm:grid-cols-[110px_minmax(0,1fr)] gap-2 items-center text-[10px] text-text-muted">
          <span>Currency (site-wide)</span>
          <span className="font-medium text-text-primary">{draft.currency}</span>
        </div>

        {/* Technology row — pinned at the top because every other
            field's outlier / helper logic is tech-conditioned. */}
        <div className="grid grid-cols-[84px_minmax(0,1fr)_auto] sm:grid-cols-[110px_minmax(0,1fr)_auto] gap-2 items-center">
          <label htmlFor="fa-technology" className="text-[11px] text-text-secondary">Campus technology</label>
          <select
            id="fa-technology"
            value={draft.technology}
            onChange={e => setTechnology(e.target.value as FinancialTechnology)}
            className="h-9 px-2 py-1 text-[12px] w-full min-w-0 border border-border bg-surface-1 text-text-primary rounded-md"
          >
            {TECH_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ProvenanceChip provenance={draft.fieldProvenance.technology} fieldKey="technology" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {SECTIONS.map(section => (
            <SectionShell
              key={section.key}
              title={section.title}
              isOpen={openSections[section.key]}
              onToggle={() => toggleSection(section.key)}
              setCount={countSetInSection(draft, section.fields)}
              totalCount={section.fields.length}
            >
              {openSections[section.key] && section.fields.map(def => (
                <FieldRow
                  key={`${seedGeneration}-${def.field}`}
                  def={def}
                  value={draft[def.field] as number | string | null}
                  provenance={draft.fieldProvenance[def.field]}
                  onChange={raw => setField(def.field, raw)}
                  outlier={
                    def.kind !== 'date'
                      ? isOutlier(draft.technology, def.field, typeof draft[def.field] === 'number' ? (draft[def.field] as number) : null)
                      : false
                  }
                  derivedHint={derivedHintFor(def.field, recomputed)}
                />
              ))}
            </SectionShell>
          ))}
        </div>

        {error && (
          <div className="text-[11px] text-danger flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          {onCancel && (
            <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          )}
          {/* With a held revision the save IS an overwrite — say so on the
              button rather than letting the loss happen quietly (audit A6). */}
          <Button
            size="sm"
            onClick={() => void handleSave()}
            disabled={saving}
            data-testid="fa-save"
          >
            {saving
              ? 'Saving…'
              : incoming
                ? 'Save (overwrites newer version)'
                : 'Save'}
          </Button>
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}

/**
 * One cross-validation finding (audit E1). Severity drives the colour; a
 * finding whose `sync` targets the FA gets a one-click fix. Intent-targeted
 * syncs are shown as text only — this form does not own Project Intent, and
 * offering a button that writes another surface from here would hide the
 * write from the tab that owns it.
 */
function CrossValidationFindingRow(props: {
  finding: CrossValidationFinding;
  onApplySync: (finding: CrossValidationFinding) => void;
}): React.JSX.Element {
  const { finding, onApplySync } = props;
  const tone =
    finding.severity === 'error'
      ? 'border-danger/60 bg-danger/10 text-danger'
      : finding.severity === 'warning'
        ? 'border-warning/60 bg-warning/10 text-warning'
        : 'border-border bg-surface-2 text-text-secondary';
  const Icon = finding.severity === 'info' ? Info : AlertTriangle;
  const canSync = finding.sync?.targetSurface === 'financialAssumptions';
  return (
    <li
      className={`flex flex-wrap items-center gap-2 text-[11px] border px-3 py-2 rounded-md ${tone}`}
      data-testid={`fa-finding-${finding.id}`}
      data-severity={finding.severity}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="text-text-primary min-w-0">{finding.message}</span>
      {finding.sync && (
        <span className="ml-auto flex items-center gap-2">
          {canSync ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onApplySync(finding)}
              data-testid={`fa-finding-sync-${finding.id}`}
            >
              {finding.sync.description}
            </Button>
          ) : (
            <span className="text-text-muted">{finding.sync.description}</span>
          )}
        </span>
      )}
    </li>
  );
}

/**
 * Per-phase technology override. Each phase can pick its OWN technology
 * (any combination across phases) or inherit the campus default. The empty
 * option ('') clears the override and falls back to the campus technology —
 * we never fabricate a concrete bucket for an inheriting phase.
 */
function PhaseTechnologyRow(props: {
  phase: FinancialPhase;
  campusTechnology: FinancialTechnology;
  onChange: (raw: string) => void;
}): React.JSX.Element {
  const { phase, campusTechnology, onChange } = props;
  // `undefined` and the 'tbd' sentinel both mean "not chosen for this phase" —
  // render them as the empty (inherit-campus) selection. A concrete per-phase
  // bucket (smr / gas-cc / …) shows as an explicit override.
  const phaseTech = phase.technology == null || phase.technology === 'tbd' ? '' : phase.technology;
  const inherits = phaseTech === '';
  const inheritedLabel =
    TECH_OPTIONS.find(o => o.value === campusTechnology)?.label ?? campusTechnology;
  const inputId = `fa-phase-${phase.phaseId}-technology`;
  return (
    <div className="grid grid-cols-[84px_minmax(0,1fr)_auto] sm:grid-cols-[110px_minmax(0,1fr)_auto] gap-2 items-center">
      <label htmlFor={inputId} className="text-[11px] text-text-secondary">
        Phase technology
      </label>
      <select
        id={inputId}
        value={phaseTech}
        onChange={e => onChange(e.target.value)}
        className={`h-8 px-2 py-1 text-[12px] w-full min-w-0 border border-border bg-surface-1 rounded-md ${
          inherits ? 'text-text-muted italic' : 'text-text-primary'
        }`}
      >
        <option value="">{`Inherit campus (${inheritedLabel})`}</option>
        {TECH_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <span className="text-[9px] uppercase tracking-wider text-text-muted whitespace-nowrap">
        {inherits ? 'inherited' : 'phase'}
      </span>
    </div>
  );
}

/**
 * Numeric (and date) field input with a focus-scoped RAW buffer.
 *
 * The display formatters (`formatFieldValue` / `formatNumeric`) round and
 * thousand-separate values for READING. Feeding that formatted string straight
 * back into a FOCUSED input destroys mid-edit text: the intermediate "7."
 * parses to 7 and re-renders as "7", eating the decimal point so the next
 * keystroke appends — "7.5" was saved as 75, "0.7" as 7. A value >= 1000 is
 * worse still: the rounded display ("1,235") is what the next edit re-parses,
 * silently dropping the fraction.
 *
 * Fix (input layer only — the formatters are untouched): while the field is
 * being edited we render EXACTLY what the user typed (`raw`), seeded on focus
 * from the FULL-PRECISION number (`String(value)`, not the rounded display).
 * Every keystroke still commits the parsed value to the draft, so live
 * recompute hints keep updating. On blur the buffer is dropped and the
 * formatted display returns — display precision for idle fields is unchanged.
 *
 * `date` inputs and the read-only derived cell keep the old behaviour exactly:
 * no buffer, always formatted.
 */
function NumericFieldInput(props: {
  id: string;
  kind: FieldKind;
  value: number | string | null;
  onChange: (raw: string) => void;
  className: string;
  readOnly?: boolean;
  ariaInvalid?: boolean;
}): React.JSX.Element {
  const { id, kind, value, onChange, className, readOnly = false, ariaInvalid } = props;
  const isDate = kind === 'date';
  /** `null` = idle (show formatted). A string = mid-edit (show it verbatim). */
  const [raw, setRaw] = useState<string | null>(null);
  const buffered = !isDate && !readOnly;
  const display = buffered && raw !== null ? raw : formatFieldValue(kind, value);
  return (
    <Input
      id={id}
      type={isDate ? 'date' : 'text'}
      inputMode={isDate ? undefined : 'decimal'}
      value={display}
      onFocus={buffered ? () => setRaw(value == null ? '' : String(value)) : undefined}
      onChange={e => {
        if (buffered) setRaw(e.target.value);
        onChange(e.target.value);
      }}
      onBlur={buffered ? () => setRaw(null) : undefined}
      className={className}
      readOnly={readOnly}
      aria-invalid={ariaInvalid}
    />
  );
}

function PhaseFieldRow(props: {
  def: PhaseFieldDef;
  phase: FinancialPhase;
  campusTechnology: FinancialTechnology;
  provenance: FieldProvenance | undefined;
  onChange: (raw: string) => void;
}): React.JSX.Element {
  const { def, phase, campusTechnology, provenance, onChange } = props;
  const value = phase[def.field] as number | string | null;
  const tech = phase.technology ?? campusTechnology;
  const outlier = def.kind !== 'date' && typeof value === 'number'
    ? isOutlier(tech, def.field as FinancialField, value)
    : false;
  const inputId = `fa-phase-${phase.phaseId}-${def.field}`;
  return (
    <div className="grid grid-cols-[84px_minmax(0,1fr)_auto] sm:grid-cols-[110px_minmax(0,1fr)_auto] gap-2 items-center">
      <label htmlFor={inputId} className="text-[11px] text-text-secondary">
        {def.label}
      </label>
      <NumericFieldInput
        id={inputId}
        kind={def.kind}
        value={value}
        onChange={onChange}
        className={`h-8 px-2 py-1 text-[12px] w-full min-w-0 tabular-nums text-right rounded-md ${
          outlier ? 'border-warning/60' : ''
        }`}
        readOnly={def.field === 'capexPerKwUsd'}
      />
      <PhaseProvenanceChip
        provenance={provenance}
        fieldKey={`phase-${phase.phaseId}-${def.field}`}
      />
    </div>
  );
}

/**
 * Compact provenance chip for an auto-populated per-phase field. Shows
 * "from BOM" / "from intent" (resolved from the derived cellReference) only
 * when `provenance.source === 'derived'`; renders nothing otherwise so a
 * user-edited (`manual`) or never-touched phase cell stays uncluttered.
 */
function PhaseProvenanceChip({
  provenance,
  fieldKey,
}: {
  provenance: FieldProvenance | undefined;
  fieldKey: string;
}): React.JSX.Element | null {
  if (!provenance || provenance.source !== 'derived') return null;
  const ref = provenance.cellReference ?? '';
  const isBom = ref.startsWith('supplyChainPlan');
  const label = isBom ? 'from BOM' : 'from intent';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded border-0 cursor-default whitespace-nowrap ${confidenceColor(provenance.confidence)}`}
          data-testid={`provenance-${fieldKey}`}
          aria-label={`auto-filled ${label}, ${provenance.confidence} confidence`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" aria-hidden />
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="text-[10px] max-w-xs">
        <div>auto-filled · {provenance.confidence}</div>
        {provenance.cellReference && (
          <div className="opacity-70 mt-0.5">{provenance.cellReference}</div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function SectionShell(props: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  setCount: number;
  totalCount: number;
  children?: React.ReactNode;
}): React.JSX.Element {
  const { title, isOpen, onToggle, setCount, totalCount, children } = props;
  return (
    <div className="border border-border rounded-md">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-state-hover text-left"
      >
        <div className="flex items-center gap-2">
          {isOpen
            ? <ChevronDown className="w-3.5 h-3.5 text-text-muted shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 text-text-muted shrink-0" />
          }
          <span className="text-[11px] font-semibold uppercase tracking-widest text-text-primary">
            {title}
          </span>
        </div>
        <span className="type-body uppercase tracking-widest text-text-muted">
          {setCount} / {totalCount} set
        </span>
      </button>
      {isOpen && (
        <div className="px-3 py-3 space-y-2 border-t border-border">
          {children}
        </div>
      )}
    </div>
  );
}

function FieldRow(props: {
  def: FieldDef;
  value: number | string | null;
  provenance: FieldProvenance | undefined;
  onChange: (raw: string) => void;
  outlier: boolean;
  derivedHint: string | null;
}): React.JSX.Element {
  const { def, value, provenance, onChange, outlier, derivedHint } = props;
  const inputId = `fa-${def.field}`;
  const isNumeric = def.kind !== 'date';
  return (
    <div className="grid grid-cols-[84px_minmax(0,1fr)_auto] sm:grid-cols-[110px_minmax(0,1fr)_auto] gap-2 items-center">
      <label htmlFor={inputId} className="text-[11px] text-text-secondary leading-snug">
        {def.label}
        <span className="ml-1 type-body uppercase tracking-widest text-text-muted">
          {unitLabel(def.kind)}
        </span>
      </label>
      <div className="min-w-0">
        <NumericFieldInput
          id={inputId}
          kind={def.kind}
          value={value}
          onChange={onChange}
          className={`h-8 px-2 py-1 text-[12px] w-full min-w-0 tabular-nums rounded-md ${
            isNumeric ? 'text-right' : ''
          } ${outlier ? 'border-warning/60' : ''}`}
          ariaInvalid={outlier}
        />
        {(def.hint || derivedHint || outlier) && (
          <div className="text-[10px] text-text-muted mt-0.5 leading-snug flex flex-col gap-0.5">
            {outlier && (
              <span className="text-warning inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 shrink-0" /> Verify — outside typical range
              </span>
            )}
            {derivedHint && (
              <span>{derivedHint}</span>
            )}
            {def.hint && !outlier && !derivedHint && (
              <span>{def.hint}</span>
            )}
          </div>
        )}
      </div>
      <ProvenanceChip provenance={provenance} fieldKey={def.field} />
    </div>
  );
}

function ProvenanceChip({
  provenance,
  fieldKey,
}: {
  provenance: FieldProvenance | undefined;
  fieldKey?: string;
}): React.JSX.Element | null {
  if (!provenance) {
    return (
      <span className="text-[9px] uppercase tracking-wider text-text-muted whitespace-nowrap">
        —
      </span>
    );
  }
  const { source, confidence } = provenance;
  const abbrev = sourceAbbrev(source);
  const fullSource = sourceFullLabel(source);
  const testId = fieldKey ? `provenance-${fieldKey}` : undefined;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded border-0 cursor-default ${confidenceColor(confidence)}`}
          data-testid={testId}
          aria-label={`${fullSource}, ${confidence} confidence`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0" aria-hidden />
          {abbrev}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="text-[10px] max-w-xs">
        <div>{fullSource} · {confidence}</div>
        {provenance.cellReference && (
          <div className="opacity-70 mt-0.5">{provenance.cellReference}</div>
        )}
        {provenance.baselineRef && (
          <div className="opacity-70 mt-0.5">{provenance.baselineRef}</div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function confidenceColor(c: FieldProvenanceConfidence): string {
  switch (c) {
    case 'high':   return 'text-success bg-success/10';
    case 'medium': return 'text-warn bg-warn/10';
    case 'low':    return 'text-text-muted bg-state-hover';
  }
}

function sourceAbbrev(source: FieldProvenanceSource): string {
  if (source.startsWith('extracted-')) return 'X';
  if (source === 'manual') return 'M';
  if (source === 'derived') return 'D';
  if (source === 'industry-baseline') return 'B';
  return '·';
}

function sourceFullLabel(source: FieldProvenanceSource): string {
  switch (source) {
    case 'extracted-named-range':     return 'extracted (named range)';
    case 'extracted-label-match':     return 'extracted (label match)';
    case 'extracted-llm':             return 'extracted (AI)';
    case 'extracted-defined-pattern': return 'extracted (PPA pattern)';
    case 'extracted-section':         return 'extracted (PPA section)';
    case 'extracted-context':         return 'extracted (site context)';
    case 'manual':                    return 'manual';
    case 'industry-baseline':         return 'industry baseline';
    case 'derived':                   return 'derived';
  }
}

function unitLabel(kind: FieldKind): string {
  switch (kind) {
    case 'percent':       return '%';
    case 'usd':           return '$';
    case 'usd-per-kw':    return '$/kW';
    case 'usd-per-mwh':   return '$/MWh';
    case 'mw':            return 'MW';
    case 'years':         return 'yr';
    case 'months':        return 'mo';
    case 'date':          return '';
    default:              return '';
  }
}

function parseFieldValue(kind: FieldKind | undefined, raw: string): number | string | null | '__INVALID__' {
  if (kind === 'date') return raw === '' ? null : raw;
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed.replace(/[$,\s%]/g, ''));
  if (!Number.isFinite(parsed)) return '__INVALID__';
  return parsed;
}

/**
 * Round a numeric value to a sensible display precision for its kind and
 * strip trailing-zero/float-noise. Prevents raw IEEE-754 artefacts (e.g.
 * `57.99999999999999`) from leaking into the input fields.
 *
 *  - `years` / `months`: integer-ish — 1 decimal max, integers shown bare.
 *  - everything else (`percent`, `number`, `usd-*`, `mw`): 2 decimals max.
 *
 * `Number(x.toFixed(n))` does the rounding-and-trim in one step:
 * `Number((57.99999999999999).toFixed(2))` → `58`, and
 * `Number((4.5).toFixed(1))` → `4.5` (no spurious `.0`).
 */
function formatNumeric(kind: FieldKind, value: number): string {
  if (!Number.isFinite(value)) return '';
  const decimals = kind === 'years' || kind === 'months' ? 1 : 2;
  return String(Number(value.toFixed(decimals)));
}

function formatFieldValue(kind: FieldKind, value: number | string | null): string {
  if (value == null) return '';
  if (kind === 'date') return String(value);
  if (typeof value === 'number') {
    // Show large dollar values with thousand-separators; smaller values get
    // rounded to a kind-appropriate precision so float noise (e.g.
    // 57.99999999999999) never reaches the field.
    if (Math.abs(value) >= 1000) {
      return Math.round(value).toLocaleString('en-US');
    }
    return formatNumeric(kind, value);
  }
  return String(value);
}

function countSetFields(fa: FinancialAssumptions, phases: FinancialPhase[]): number {
  // Campus-level: technology (tbd/null = unset) + campus section fields.
  let n = fa.technology && fa.technology !== 'tbd' ? 1 : 0;
  for (const section of SECTIONS) {
    n += countSetInSection(fa, section.fields);
  }
  // Per-phase: count each populated countable field across every phase, so
  // filling Phase 1 (or an auto-pull) reflects in the header counter.
  for (const phase of phases) {
    for (const field of COUNTABLE_PHASE_FIELDS) {
      const v = phase[field];
      if (v !== null && v !== undefined && v !== '') n += 1;
    }
  }
  return n;
}

function countSetInSection(fa: FinancialAssumptions, fields: FieldDef[]): number {
  let n = 0;
  for (const def of fields) {
    const v = fa[def.field];
    if (v !== null && v !== undefined && v !== '') n += 1;
  }
  return n;
}

function derivedHintFor(field: FinancialField, r: ReturnType<typeof recompute>): string | null {
  if (field === 'capexPerKwUsd' && r.capexPerKwUsd != null) {
    return `would derive to ${Math.round(r.capexPerKwUsd).toLocaleString('en-US')} $/kW`;
  }
  if (field === 'waccPct' && r.waccPct != null) {
    return `would derive to ${r.waccPct.toFixed(2)}%`;
  }
  if (field === 'lcoeNominalPerMwhUsd' && r.lcoeNominalPerMwhUsd != null) {
    return `would derive to ${r.lcoeNominalPerMwhUsd.toFixed(2)} $/MWh`;
  }
  if (field === 'lcoeRealPerMwhUsd' && r.lcoeRealPerMwhUsd != null) {
    return `would derive to ${r.lcoeRealPerMwhUsd.toFixed(2)} $/MWh`;
  }
  if (field === 'npvUsd' && r.npvUsd != null) {
    const sign = r.npvUsd >= 0 ? '+' : '−';
    return `would derive to ${sign}$${Math.abs(Math.round(r.npvUsd / 1_000_000))}M`;
  }
  if (field === 'projectIrrPct' && r.projectIrrPct != null) {
    return `would derive to ${r.projectIrrPct.toFixed(2)}%`;
  }
  if (field === 'paybackYears' && r.paybackYears != null) {
    return `would derive to ${r.paybackYears.toFixed(1)} yr`;
  }
  return null;
}

function formatRelativeTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';
    const ms = Date.now() - then;
    const min = Math.round(ms / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const days = Math.round(hr / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  } catch {
    return '';
  }
}

/** Test-only surface for the pure display formatters (no React render). */
export const __test__ = {
  formatFieldValue,
  formatNumeric,
};
